import React, {useState} from 'react';
import {DialogOverlay, DialogContent} from '@reach/dialog';
import {compose, graphql} from 'react-apollo';
import {injectStripe, CardElement} from 'react-stripe-elements';
import {animated, useTransition} from 'react-spring';
import {useFormState} from 'react-use-form-state';
import gql from 'graphql-tag';

import {Button, EqualColumns, Input, Label, Loading} from 'ui-kit';

import * as styles from './PaymentDetailsModal.module.css';

const AnimatedDialogOverlay = animated(DialogOverlay);
const AnimatedDialogContent = animated(DialogContent);

function PaymentDetailsModal({id, show, onDismiss, onSubmit, stripe, mutate}) {
  const [loading, setLoading] = useState(false);
  const [formState, {text}] = useFormState({
    billingName: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
  });
  const transitions = useTransition(show, null, {
    from: {opacity: 0, y: -20},
    enter: {opacity: 1, y: 0},
    leave: {opacity: 0, y: -20},
    config: {duration: 100},
  });

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);

    const {token, error} = await stripe.createToken({
      name: formState.values.billingName,
      address_line1: formState.values.billingAddress1,
      address_line2: formState.values.billingAddress2,
      address_city: formState.values.billingCity,
      address_state: formState.values.billingState,
      address_zip: formState.values.billingZip,
    });

    if (error != null) {
      setLoading(false);
      return;
    }

    try {
      const res = await mutate({
        variables: {
          input: {
            membershipV2Id: id,
            billingName: formState.values.billingName,
            billingAddress1: formState.values.billingAddress1,
            billingAddress2: formState.values.billingAddress2,
            billingCity: formState.values.billingCity,
            billingState: formState.values.billingState,
            billingZip: formState.values.billingZip,
            stripeCardToken: token.id,
          },
        },
      });

      console.log(res);

      onSubmit();
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return transitions.map(({item: show, key, props: {opacity, y}}) => (
    <AnimatedDialogOverlay
      key={key}
      style={{opacity}}
      onDismiss={onDismiss}
      isOpen={show}
    >
      <AnimatedDialogContent
        className={styles.Dialog}
        style={{
          transform: y.interpolate(y => `translate3d(0px, ${y}px, 0px)`),
        }}
      >
        <h2 className={styles.Heading}>New payment source</h2>

        <form method="post" onSubmit={handleSubmit}>
          <EqualColumns columns={1}>
            <div>
              <Label>Credit card details</Label>
              <div className={styles.CardElement}>
                <CardElement
                  style={{
                    base: {
                      fontWeight: 500,
                      fontSize: '16px',
                      fontSmoothing: 'antialiased',
                    },
                  }}
                />
              </div>
            </div>
          </EqualColumns>

          <EqualColumns columns={1}>
            <Input label="Name on card" noPadding {...text('billingName')} />
          </EqualColumns>

          <EqualColumns>
            <Input
              label="Billing address 1"
              noPadding
              {...text('billingAddress1')}
            />
            <Input
              label="Billing address 2"
              noPadding
              {...text('billingAddress2')}
            />
          </EqualColumns>

          <EqualColumns columns={3}>
            <Input label="Billing city" noPadding {...text('billingCity')} />
            <Input label="Billing state" noPadding {...text('billingState')} />
            <Input label="Billing zip" noPadding {...text('billingZip')} />
          </EqualColumns>

          <div className={styles.ButtonGroup}>
            <Button disabled={loading}>
              {loading ? <Loading immediate /> : 'Submit'}
            </Button>
            <Button
              disabled={loading}
              color="light"
              onClick={onDismiss}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </AnimatedDialogContent>
    </AnimatedDialogOverlay>
  ));
}

export default compose(
  injectStripe,
  graphql(
    gql`
      mutation UpdateBillingDetails(
        $input: UpdateMembershipV2BillingDetailsInput!
      ) {
        updateMembershipV2BillingDetails(input: $input) {
          errors {
            path
            message
          }
          membership {
            id
          }
        }
      }
    `,
  ),
)(PaymentDetailsModal);
